import { Input, Button, Body, Flex, Pagination } from '@cognite/cogs.js';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useUrl } from 'hooks/useUrls';
import { InfoMessage, Spinner } from 'components';
import {
  ProjectListItem,
  useGetAddSolutionsForDrawerQuery,
} from 'hooks/projects';
import { PAGINATION_MAX_SIZE } from 'utils/config';
import { getPageItems } from 'utils/misc';
import { SolutionCard } from './components/SolutionCard';

export interface SelectedProjectList {
  [key: string]: {
    name: string;
    version?: string;
    tenant: string;
    space: string;
  };
}

const filterBySearchValue = (
  schemasList: ProjectListItem[],
  searchValue?: string
) => {
  if (!searchValue) return schemasList;

  const searchString = searchValue.trim().toLowerCase();

  return schemasList?.filter((schema) => {
    const schemaName = schema.name.toLowerCase();
    return schemaName.includes(searchString);
  });
};

export const SolutionCardsList = ({
  existingSolutionIds,
  selectedProjectList,
  onHandleProjectChange,
}: {
  existingSolutionIds: string[];
  selectedProjectList: SelectedProjectList;
  onHandleProjectChange: (
    project: ProjectListItem,
    state: boolean,
    version?: string
  ) => void;
}) => {
  const [searchFilter, setSearchFilter] = useState<string | undefined>('');
  const [page, setPage] = useState<number>(1);
  const tenant = useUrl.Tenant();
  const PAGINATION_PAGE_SIZE = 100;

  const { data: dataModelsData, loading: loadingProjects } =
    useGetAddSolutionsForDrawerQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        limit: PAGINATION_MAX_SIZE,
        cursor: null,
        tenant,
        existingSolutionIds,
      },
    });
  const dataModels = dataModelsData.Project;
  // Filter out the schemas already added to the project
  const addableSchemas = dataModels.filter(
    (dm) =>
      !existingSolutionIds.some((id) => id.startsWith(`${dm.space}:${dm.id}`))
  );

  // Filter out the schemas based on the "searchString"
  const filteredSchemas = filterBySearchValue(addableSchemas, searchFilter);
  const visibleSchemas = getPageItems(filteredSchemas, page);

  const renderSearchCards = () => {
    if (loadingProjects)
      return (
        <CenteredContainer>
          <Spinner />
        </CenteredContainer>
      );

    if (visibleSchemas.length) {
      return visibleSchemas.map(
        (schema) =>
          schema && (
            <SolutionCard
              project={schema}
              key={schema.id}
              active={!!selectedProjectList[schema.id]}
              selectedVersion={selectedProjectList[schema.id]?.version}
              onHandleProjectChange={onHandleProjectChange}
            />
          )
      );
    }

    return <InfoMessage type="Search" title="No schemas found" />;
  };

  return (
    <div>
      <StyledInput
        placeholder="Search schema"
        type="search"
        icon="Search"
        value={searchFilter}
        iconPlacement="left"
        onChange={(e) => setSearchFilter(e.target.value)}
      />
      <StyledContainer>{renderSearchCards()}</StyledContainer>

      <Pagination
        totalPages={Math.max(
          1,
          Math.ceil(filteredSchemas.length / PAGINATION_PAGE_SIZE)
        )}
        currentPage={page}
        hideItemsPerPage
        onPageChange={(newPage: number) => setPage(newPage)}
        hidden={filteredSchemas.length === 0}
        itemsPerPage={PAGINATION_PAGE_SIZE}
      />
    </div>
  );
};

const StyledContainer = styled.div`
  margin-top: 2rem;
`;

const StyledInput = styled(Input)`
  width: 100%;
  .cogs-input {
    border-radius: 5px;
  }
`;

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
`;
